import { Asset } from "@hermes/api-model-asset";
import {
    Alternatives,
    Facet,
    Filter,
    FilterGroup,
    ProductCollection,
    ProductCollectionResponse,
} from "@hermes/api-model-catalog";
import { EditoResponse } from "@hermes/api-model-editorial";
import {
    GridProduct as ProductFromGrid,
    GridVariant,
    Product,
} from "@hermes/api-model-product";
import { isProductEngravable } from "@hermes/fragments/product-utils";
import { ProductCategory } from "@hermes/states/personalization-page";
import { AssetMediaType, AssetTag } from "@hermes/utils-generic/constants";

import { getVariantAsset } from "../helpers/grid-product.helper";
import {
    GridAlternatives,
    GridAsset,
    GridData,
    GridEdito,
    GridFacet,
    GridFilter,
    GridFilterGroup,
    GridProduct,
} from "../model/grid.model";

/**
 * these mappers are used to transform Grid Model complexe object class into simpler object
 *
 * Equivalents :
 * - GridData <=> ProductCollecionResponse
 * - GridFilter <=> Filter
 * - GridFilterGroup <=>  FilterGroup
 * - GridProduct <=> Product
 * - GridEdito <=> Edito
 * - GridAlternatives <=> Alternatives
 * - GridFacet <=> Facet
 */

export const gridEditosFromEditoResponse = (
    editos: EditoResponse[] | undefined,
): GridEdito[] =>
    editos
        ? editos.map((edito: EditoResponse) => ({
              nodeId: edito.nodeId,
              nodeUrl: edito.nodeUrl,
              contentTitle: edito.contentTitle,
              contentBody: edito.contentBody,
              mediaType: edito.mediaType,
              imageRetouched: edito.imageRetouched,
              imageMobileUrl: edito.imageMobileUrl,
              imageDesktopUrl: edito.imageDesktopUrl,
              imageDesktopAlt: edito.imageDesktopAlt,
              videoAlt: edito.videoAlt,
              alwaysShowControls: edito.alwaysShowControls,
              videoLoopMuteAutoplay: edito.videoLoopMuteAutoplay,
              videoUrl: edito.videoUrl,
              imageMobileAlt: edito.imageMobileAlt,
              token: edito.token,
          }))
        : [];

export const gridDataFromProductCollectionResponse = (
    response: ProductCollectionResponse,
): GridData => ({
    total: response.total,
    url: response.url,
    redirectionUrl: response.redirectionUrl,
    products: gridProductsFromProductCollection(response.productCollection),
    alternatives: gridAlternativesFromAlternatives(response.alternatives),
    filterGroups: gridFilterGroupsFromFilterGroups(response.filters),
    editos: gridEditosFromEditoResponse(response.editorials),
    facet: gridFacetFromFacet(response.facet),
    isHeroProduct: response.isHeroProduct,
});

export const gridProductsFromProductCollection = (
    productCollection: ProductCollection,
): GridProduct[] => {
    if (!productCollection?.items || productCollection.items.length === 0) {
        return [];
    }

    return productCollection.items.map((product: ProductFromGrid) => ({
        sku: product.sku,
        title: product.title || "",
        productCode: product.productCode,
        departmentCode: product.departmentCode,
        persoProductType: product.persoProductType as ProductCategory,
        slug: product.slug,
        url: product.url,
        price: product.price,
        priceType: product.priceType,
        familyCode: product.familyCode,
        avgColor: product.avgColor,
        isColorless: product.isColorless,
        color: product.avgColor,
        size: product.size,
        divisionCode: product.divisionCode,
        personalize: product.personalize,
        massVolume: product.massVolume,
        hasMoreColors: product.moreColors,
        stock: {
            ecom: product?.stock !== undefined && product.stock.ecom,
            retail: product?.stock !== undefined && product.stock.retail,
            displayOnly:
                product?.stock?.displayOnly !== undefined &&
                product.stock.displayOnly,
            hasVariantInEcomStock:
                product?.stock !== undefined &&
                product.stock.hasVariantInEcomStock,
        },
        assets: gridAssetsFromAssets(product.assets),
        isVto: product.isVto,
        vtoType: product.vtoType,
        isEngravable: product.isEngravable,
        productSetType: product.productSetType,
        labelDisplayOnly: product.labelDisplayOnly || undefined,
        displayMode: product.displayMode || undefined,
        gridAssetsVariants: gridAssetsVariantsFromgridVariant(
            product.sku,
            product.gridVariants,
        ),
        templateType: product.templateType,
        quickBuyProperties: product.quickBuyProperties,
        displayNameList: product.displayNameList,
    }));
};

/**
 * Converts a Product to GridProduct, specifically for analytics use.
 * This function is currently used for analytics, where the `gridAssetsVariants` field is not needed.
 * @param product Product object.
 * @returns A GridProduct from Product, with the `gridAssetsVariants` field undefined.
 */
export const getGridProductFromProduct = (
    gridProduct: Product,
): GridProduct => ({
    sku: gridProduct.sku,
    title: gridProduct.title || "",
    productCode: gridProduct.productCode,
    departmentCode: gridProduct.departmentCode,
    persoProductType: gridProduct.persoProductType as ProductCategory,
    slug: gridProduct.slug,
    url: gridProduct.url,
    price: gridProduct.price,
    priceType: gridProduct.priceType,
    familyCode: gridProduct.familyCode,
    avgColor: gridProduct.avgColor,
    isColorless: gridProduct.isColorless,
    color: gridProduct.avgColor,
    size: gridProduct.size,
    divisionCode: gridProduct.divisionCode,
    personalize: gridProduct.personalize,
    massVolume: gridProduct.massVolume,
    hasMoreColors: gridProduct.moreColors,
    stock: {
        ecom: gridProduct?.stock !== undefined && gridProduct.stock.ecom,
        retail: gridProduct?.stock !== undefined && gridProduct.stock.retail,
        displayOnly:
            gridProduct?.stock?.displayOnly !== undefined &&
            gridProduct.stock.displayOnly,
        hasVariantInEcomStock:
            gridProduct?.stock !== undefined &&
            gridProduct.stock.hasVariantInEcomStock,
    },
    assets: gridAssetsFromAssets(gridProduct.assets),
    isVto: gridProduct.isVto,
    vtoType: gridProduct.vtoType,
    isEngravable: Boolean(
        gridProduct.simpleAttributes?.engravingOptions &&
            isProductEngravable(gridProduct),
    ),
    productSetType: gridProduct.productSetType,
    labelDisplayOnly: gridProduct.labelDisplayOnly || undefined,
    displayMode: gridProduct.displayMode || undefined,
    gridAssetsVariants: [],
    templateType: gridProduct.templateType,
    quickBuyProperties: undefined,
});

export const gridAssetsFromAssets = (
    assets: Asset[] | undefined,
): GridAsset[] => {
    if (!(assets && assets.length > 0)) {
        return [];
    }

    return assets.map((asset: Asset) => ({
        url: asset.url,
        title: asset.title || "",
        type: asset.type || "",
        posterUrl: asset.posterUrl || "",
        tag: asset.tag,
    }));
};

export const gridAssetsVariantsFromgridVariant = (
    mainSku: string,
    gridVariants: GridVariant[] | undefined,
): GridAsset[] => {
    if (!(gridVariants && gridVariants.length > 0)) {
        return [];
    }

    // Move to the top the current variant
    const indexMainSkuVariant = gridVariants.findIndex(
        (variant) => variant.sku === mainSku,
    );
    if (indexMainSkuVariant !== -1) {
        const removeElement = gridVariants.splice(indexMainSkuVariant, 1)[0];
        gridVariants.unshift(removeElement);
    }

    return gridVariants.map((gridVariant: GridVariant) => ({
        url: getVariantAsset(gridVariant.sku),
        codeRgb: gridVariant.codeRgb,
        title: gridVariant.accessibilityLabel,
        type: AssetMediaType.Image,
        tag: AssetTag.THUMBNAIL,
    }));
};

export const gridAlternativesFromAlternatives = (
    alternatives: Alternatives,
): GridAlternatives => {
    if (!alternatives) {
        return {} as GridAlternatives;
    }
    return {
        originalSearch: alternatives.originalSearch,
        resultsFor: alternatives.resultsFor,
        total: alternatives.total,
    };
};

export const gridFiltersFromFilters = (filters: Filter[]): GridFilter[] => {
    if (!(filters && filters.length > 0)) {
        return [];
    }
    return filters.map((filter: Filter) => ({
        name: filter.name,
        selected: filter.selected,
        total: filter.total,
        urlParams: filter.urlParams,
        value: filter.value,
        filterGroupName: filter.filterGroupName,
        facetIsCrawlable: filter.facetIsCrawlable === true,
        combinedFilterImage: filter.combinedFilterImage,
    }));
};

export const gridFilterGroupsFromFilterGroups = (
    filterGroups: FilterGroup[],
): GridFilterGroup[] => {
    if (!(filterGroups && filterGroups.length > 0)) {
        return [];
    }

    return filterGroups.map((filterGroup: FilterGroup) => ({
        name: filterGroup.name,
        attribute: filterGroup.attribute,
        displayHint: filterGroup.displayHint,
        presentation: filterGroup.presentation,
        isGridHighlight: filterGroup.isGridHighlight,
        facetUrlParameterName: filterGroup.facetUrlParameterName ?? "",
        filters: gridFiltersFromFilters(filterGroup.filters),
    }));
};

export const gridFacetFromFacet = (facet: Facet | undefined): GridFacet => {
    if (!facet) {
        return {} as GridFacet;
    }

    return {
        title: facet.title,
        name: facet.name,
        path: facet.path,
        altPaths: facet.altPaths,
        error: facet.error,
    };
};
